import { useEffect, useRef, useState, useMemo } from "react";

// load the script exactly once per page
function loadTurnstile() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    let s = document.querySelector('script[data-turnstile]');
    if (!s) {
      s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.defer = true;
      s.async = true;
      s.dataset.turnstile = "1";
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => resolve(window.turnstile));
    s.addEventListener("error", reject, { once: true });
  });
}

export default function TurnstileBox({
  siteKey,
  onToken,
  options = {},          // { theme: "auto", action: "signup", cdata: "...", appearance: "always" }
  className = "",
}) {
  const mountRef = useRef(null);
  const widgetIdRef = useRef(null);
  const mountedOnceRef = useRef(false); // protect against StrictMode double-mount
  const [ready, setReady] = useState(false);

  // Freeze options so TS API doesn't see a new object mid-flight
  const frozenOptions = useMemo(() => ({
    theme: "auto",
    retry: "auto",
    ...options,
  }), [options?.action, options?.appearance, options?.theme]); // only react to specific props

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!siteKey || !mountRef.current) return;

      // Avoid re-rendering the widget in StrictMode duplicate effects
      if (mountedOnceRef.current && widgetIdRef.current) {
        setReady(true);
        return;
      }

      const ts = await loadTurnstile();
      if (cancelled) return;

      if (!widgetIdRef.current && mountRef.current) {
        widgetIdRef.current = ts.render(mountRef.current, {
          sitekey: siteKey,
          callback: (token) => onToken?.(token),
          "error-callback": () => onToken?.(null),
          "timeout-callback": () => onToken?.(null),
          ...frozenOptions,
        });
        mountedOnceRef.current = true;
        setReady(true);
      }
    })();

    // cleanup ONLY on real unmount
    return () => {
      cancelled = true;
      // do NOT remove the widget unless the component actually unmounts
      // React StrictMode will call this cleanup after the first mount,
      // but we keep the widgetIdRef so it won't re-create next time.
    };
  // IMPORTANT: do not include onToken or the full options object here
  }, [siteKey, frozenOptions.theme, frozenOptions.action, frozenOptions.appearance]);

  return <div ref={mountRef} className={className} aria-busy={!ready} />;
}
